import StyledButton from "components/shared/StyledButton";
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import ScopeContext from "containers/ScopeContext";
import { formatLocaleDateString } from "helpers/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ExportItemProps } from "./types";
import { ALL_PERIODS } from "./types";

const ExportItem = ({ config, isPending, periods, defaultPeriod }: ExportItemProps) => {
    const [t] = useTranslation();
    const { label, requiresPeriod = false, requiresDateRange = false, allowAllPeriods = false, action } = config;

    const [selectedPeriod, setSelectedPeriod] = useState<number>(defaultPeriod ?? ALL_PERIODS);
    const [startDate, setStartDate] = useState<Date>(new Date(new Date().setMonth(new Date().getMonth() - 1)));
    const [endDate, setEndDate] = useState<Date>(new Date());

    const handleExport = () => {
        action({
            period: selectedPeriod,
            startDate,
            endDate,
        });
    };

    return (
        <div className="setting-buttons">
            <div className="setting-left">
                <div className="setting-title">{t(`Settings.Export${label}`)}</div>
                <div className="setting-description">{t(`Settings.Export${label}Description`)}</div>
            </div>
            <div className="setting-right">
                {/* Period Selector */}
                {requiresPeriod && (
                    <StyledSelect value={selectedPeriod} onChange={(e) => setSelectedPeriod(Number(e.target.value))}>
                        {allowAllPeriods && (
                            <StyledMenuItem value={ALL_PERIODS}>{t("Settings.AllPeriods")}</StyledMenuItem>
                        )}
                        {periods.map((period, i) => (
                            <StyledMenuItem key={i} value={period.id}>
                                {formatLocaleDateString({
                                    date: period.periodStart,
                                    options: { numeric: true, timeZone: "UTC" },
                                })}{" "}
                                {">"}{" "}
                                {formatLocaleDateString({
                                    date: period.periodEnd,
                                    options: { numeric: true, timeZone: "UTC" },
                                })}
                            </StyledMenuItem>
                        ))}
                    </StyledSelect>
                )}

                {/* Date Range Pickers */}
                {requiresDateRange && (
                    <div style={{ display: "flex", gap: "8px" }}>
                        <StyledDatePicker
                            value={startDate}
                            inputFormat="dd/MM/yyyy"
                            onChange={(value) => setStartDate(value)}
                            renderInput={(params) => <StyledTextField {...params} />}
                        />
                        <StyledDatePicker
                            value={endDate}
                            inputFormat="dd/MM/yyyy"
                            onChange={(value) => setEndDate(value)}
                            renderInput={(params) => <StyledTextField {...params} />}
                        />
                    </div>
                )}

                {/* Export Button */}
                <ScopeContext extraConditions={[!isPending]} errorProps={{ disabled: true }}>
                    {(props) => (
                        <StyledButton buttonType="primary" onClick={handleExport} {...props}>
                            {t("Export.ExportButton")}
                        </StyledButton>
                    )}
                </ScopeContext>
            </div>
        </div>
    );
};

export default ExportItem;
